import {
  Alert,
  List,
  SimpleSelect,
  SwitchInput,
  TextAreaInput,
  TextInput,
} from 'corso-admin-ui';
import { Pegasus } from 'corso-types';
import { Controller } from 'react-hook-form';
import { useAutomationFormContext } from '~/features/settings/automations/hooks/useAutomationForm';

type AlertVariant =
  Pegasus.EventSpcSlaNotification['params']['alert']['variant'];

const alertVariants = ['info', 'warning', 'danger'] satisfies AlertVariant[];

const alertVariantLabel = {
  info: 'Info',
  warning: 'Warning',
  danger: 'Danger',
} as const satisfies Record<AlertVariant, string>;

const alertVariantOptions = alertVariants.map((variant) => ({
  value: variant,
  label: alertVariantLabel[variant],
}));

export function EventShippingClaimSlaAlert() {
  const { control } = useAutomationFormContext();

  return (
    <div className="flex flex-col gap-4">
      <Alert heading="Writing a Useful Alert">
        <List
          className="mt-2"
          columns={1}
          items={[
            'Name the problem in the title; say what to do about it in the message.',
            'Reserve Danger for something that has to be handled before the claim is resolved.',
            'More than one matching alert can be enabled; they stack by severity.',
          ]}
        >
          {(guidance) => <List.Item key={guidance}>{guidance}</List.Item>}
        </List>
      </Alert>
      <Controller
        control={control}
        name="event.params.alert.variant"
        render={({ field, fieldState }) => (
          <SimpleSelect
            label="Severity"
            details="How prominently the alert is displayed on the shipping claim. Danger sorts above Warning, which sorts above Info."
            options={alertVariantOptions}
            value={field.value}
            onChange={field.onChange}
            error={fieldState.error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="event.params.alert.title"
        render={({ field, fieldState }) => (
          <TextInput
            id="sla-alert-title"
            label="Title"
            details="The heading of the alert. Name the problem in a few words, e.g. 'Approval required over $50'."
            maxLength={40}
            required
            {...field}
            error={fieldState.error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="event.params.alert.message"
        render={({ field, fieldState }) => (
          <TextAreaInput
            id="sla-alert-message"
            label="Message"
            details="What the agent should do about it, in one or two sentences."
            maxLength={150}
            required
            {...field}
            error={fieldState.error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="event.params.shouldBlockFinalization"
        render={({ field }) => (
          <SwitchInput
            id="sla-alert-block-finalization"
            label="Block Finalization"
            details="Disable Finalize Claim while this alert shows. Manual refunds and reorders stay available."
            checked={!!field.value}
            onChange={field.onChange}
          />
        )}
      />
    </div>
  );
}
